import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CreditCard, AlertCircle, Calendar, Package, ChevronRight, Ban } from "lucide-react";
import { API } from "@/lib/api";
import { getToken } from "@/lib/auth";
import { getGaClientId } from "@/lib/analytics";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-ZA", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return iso; }
};

export default function BillingTab() {
  const [me, setMe] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/billing/me`, { headers: { Authorization: `Bearer ${getToken()}` } });
      setMe(r.data);
    } catch {
      toast.error("Could not load billing");
    }
  };
  useEffect(() => { load(); }, []);

  const upgrade = (tier) => {
    window.location.href = `/pricing#${tier}`;
  };

  const checkoutNow = async (tier) => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/billing/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ tier, ga_client_id: getGaClientId() }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || "Checkout failed");
      const html = await res.text();
      document.open(); document.write(html); document.close();
    } catch (e) {
      toast.error(e.message || "Could not start checkout");
      setBusy(false);
    }
  };

  const cancel = async () => {
    if (!window.confirm("Cancel subscription? Your listings stay visible but you won't be able to add or edit until you re-subscribe.")) return;
    setBusy(true);
    try {
      await axios.post(`${API}/billing/cancel`, {}, { headers: { Authorization: `Bearer ${getToken()}` } });
      toast.success("Subscription cancelled");
      load();
    } catch {
      toast.error("Could not cancel");
    } finally {
      setBusy(false);
    }
  };

  if (!me) return <div className="text-sm text-neutral-500 font-mono-tech">[ Loading billing... ]</div>;

  const tier = me.tier_info;
  const limit = me.listings_limit;
  const used = me.listings_used;
  const pct = limit ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const status = me.effective_status;

  return (
    <div className="max-w-2xl space-y-6" data-testid="billing-tab">
      {/* Status banner */}
      {status === "trial" && (
        <div className="border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-3" data-testid="billing-trial-banner">
          <Calendar size={18} className="text-emerald-700 mt-0.5" />
          <div className="flex-1">
            <div className="font-bold text-emerald-900">
              Free trial — {me.trial_days_remaining} day{me.trial_days_remaining === 1 ? "" : "s"} remaining
            </div>
            <div className="text-xs text-emerald-800/80 mt-0.5">
              First charge of R{tier.price_zar} on {fmtDate(me.trial_end)}. {me.payfast_token ? "Card on file." : "Add a card to keep your account active after trial."}
            </div>
          </div>
        </div>
      )}
      {status === "past_due" && (
        <div className="border border-red-200 bg-red-50 p-4 flex items-start gap-3" data-testid="billing-pastdue-banner">
          <AlertCircle size={18} className="text-red-700 mt-0.5" />
          <div className="flex-1">
            <div className="font-bold text-red-900">Subscription past due</div>
            <div className="text-xs text-red-800/80 mt-0.5">
              Your trial has ended. Add a card to keep listing parts. Existing listings remain visible.
            </div>
          </div>
        </div>
      )}
      {status === "cancelled" && (
        <div className="border border-neutral-200 bg-neutral-50 p-4 flex items-start gap-3" data-testid="billing-cancelled-banner">
          <Ban size={18} className="text-neutral-500 mt-0.5" />
          <div className="flex-1">
            <div className="font-bold text-neutral-900">Subscription cancelled</div>
            <div className="text-xs text-neutral-600 mt-0.5">Re-subscribe anytime to keep adding parts.</div>
          </div>
        </div>
      )}

      {/* Plan card */}
      <div className="border border-neutral-200 p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-mono-tech text-neutral-500">Current plan</div>
            <div className="font-display text-3xl font-extrabold leading-none mt-1">{tier.name}</div>
            <div className="text-sm text-neutral-500 mt-1">
              {tier.price_zar === null ? "Custom pricing" : `R${tier.price_zar.toLocaleString()}/month`}
            </div>
          </div>
          <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 ${
            status === "active" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" :
            status === "trial" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" :
            status === "past_due" ? "bg-red-50 text-red-700 border border-red-200" :
            "bg-neutral-50 text-neutral-500 border border-neutral-200"
          }`}>{status.replace("_", " ")}</span>
        </div>

        {/* Listings progress */}
        <div className="mt-5">
          <div className="flex items-center justify-between text-xs mb-1.5">
            <span className="text-neutral-500 flex items-center gap-1.5"><Package size={12} /> Listings used</span>
            <span className="font-mono-tech text-neutral-900 font-bold">
              {used}{limit !== null ? ` / ${limit}` : " / ∞"}
            </span>
          </div>
          {limit !== null && (
            <div className="h-2 bg-neutral-100">
              <div className={`h-full ${pct >= 100 ? "bg-red-500" : pct >= 80 ? "bg-amber-500" : "bg-[#FF5100]"}`} style={{ width: `${pct}%` }} />
            </div>
          )}
        </div>

        {/* Dates */}
        <div className="grid grid-cols-2 gap-3 mt-5 text-sm">
          {status === "trial" && (
            <>
              <Info label="Trial ends" value={fmtDate(me.trial_end)} />
              <Info label="First charge" value={fmtDate(me.trial_end)} />
            </>
          )}
          {status === "active" && (
            <>
              <Info label="Last paid" value={fmtDate(me.last_payment_at)} />
              <Info label="Next charge" value={fmtDate(me.current_period_end)} />
            </>
          )}
        </div>

        {/* Actions */}
        <div className="mt-5 flex flex-wrap gap-2 pt-5 border-t border-neutral-100">
          {status !== "cancelled" && status !== "past_due" && me.tier !== "premium" && (
            <button
              data-testid="billing-upgrade-btn"
              onClick={() => upgrade(me.tier === "starter" ? "standard" : "premium")}
              className="px-4 h-10 bg-[#FF5100] hover:bg-[#E64A00] text-white text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1.5"
            >
              Upgrade plan <ChevronRight size={12} />
            </button>
          )}
          {(status === "past_due" || status === "cancelled" || !me.payfast_token) && (
            <button
              data-testid="billing-add-card-btn"
              onClick={() => checkoutNow(me.tier === "enterprise" ? "starter" : me.tier)}
              disabled={busy}
              className="px-4 h-10 bg-[#FF5100] hover:bg-[#E64A00] text-white text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1.5"
            >
              <CreditCard size={12} /> {status === "cancelled" ? "Re-subscribe" : "Add card / activate"}
            </button>
          )}
          <button
            onClick={() => upgrade(me.tier)}
            className="px-4 h-10 border border-neutral-200 hover:border-neutral-300 text-xs font-bold uppercase tracking-wider"
          >
            See all plans
          </button>
          {(status === "active" || status === "trial") && me.payfast_token && (
            <button
              data-testid="billing-cancel-btn"
              onClick={cancel}
              disabled={busy}
              className="px-4 h-10 border border-neutral-200 hover:border-red-300 hover:text-red-700 text-xs font-bold uppercase tracking-wider"
            >
              Cancel subscription
            </button>
          )}
        </div>
      </div>

      <div className="text-[11px] text-neutral-500 font-mono-tech tracking-wider">
        BILLED IN ZAR · POWERED BY PAYFAST · SECURE PCI-DSS LEVEL 1
      </div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] font-mono-tech text-neutral-500">{label}</div>
      <div className="font-medium mt-0.5">{value}</div>
    </div>
  );
}
